import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllDevices,
  createDevice,
  updateDevice,
  deleteDevice,
  subscribeToAllDevices
} from '../services/vpsDisplays';
import { getAllCompanies } from '../services/vpsCompanies';
import { getAllTimers } from '../services/vpsApi';
import './DeviceManagement.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://multi-counter.bancheng.tech/api';

function DeviceManagement() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [timers, setTimers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [filterCompany, setFilterCompany] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    companyId: '',
    deviceCode: '',
    deviceName: '',
    timerId: null
  });

  useEffect(() => {
    loadData();

    // Subscribe to real-time device updates
    const unsubscribe = subscribeToAllDevices((devicesData) => {
      setDevices(devicesData);
    });

    return () => unsubscribe();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [devicesData, companiesData, timersData] = await Promise.all([
        getAllDevices(),
        getAllCompanies(),
        getAllTimers()
      ]);

      setDevices(devicesData);
      setCompanies(companiesData);
      setTimers(timersData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingDevice) {
        // Update existing device
        await updateDevice(editingDevice.id, {
          deviceName: formData.deviceName,
          timerId: formData.timerId || null
        });
        alert('設備更新成功');
      } else {
        // Create new device
        const newDevice = await createDevice({
          companyId: formData.companyId,
          deviceCode: formData.deviceCode,
          deviceName: formData.deviceName,
          timerId: formData.timerId || null,
          slideshowUrls: [],
          animationUrl: null
        });
        console.log('✅ 新增設備成功，收到的資料:', newDevice);
        console.log('✅ 顯示器連結:', newDevice?.顯示器連結);
        alert('設備建立成功');
      }

      // Reset form
      setShowCreateModal(false);
      setEditingDevice(null);
      setFormData({
        companyId: '',
        deviceCode: '',
        deviceName: '',
        timerId: null
      });

      // Reload data to ensure we get the latest display link
      await loadData();
    } catch (error) {
      console.error('Error saving device:', error);
      alert('操作失敗: ' + error.message);
    }
  };

  const handleEdit = (device) => {
    setEditingDevice(device);
    setFormData({
      companyId: device.公司ID,
      deviceCode: device._id,
      deviceName: device.名稱,
      timerId: ''
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (deviceId) => {
    if (!window.confirm('確定要刪除此設備嗎？')) {
      return;
    }

    try {
      await deleteDevice(deviceId);
      alert('設備刪除成功');
    } catch (error) {
      console.error('Error deleting device:', error);
      alert('刪除失敗: ' + error.message);
    }
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setEditingDevice(null);
    setFormData({
      companyId: '',
      deviceCode: '',
      deviceName: '',
      timerId: null
    });
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c._id === companyId);
    return company ? company.名稱 : '未知公司';
  };

  const getTimerName = (timerId) => {
    const timer = timers.find(t => t._id === timerId || t.id === timerId);
    return timer ? timer.name : null;
  };

  const filteredDevices = filterCompany === 'all'
    ? devices
    : devices.filter(d => d.公司ID === filterCompany);

  if (loading) {
    return (
      <div className="device-management">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await fetch(`${API_URL}/webhook/sheets-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manual: true })
      });
      const result = await response.json();
      if (result.success) {
        alert(`同步完成！已同步 ${result.recordsSynced} 筆資料`);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      alert('同步失敗：' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  // Trigger display refresh via WebSocket
  const handleRefreshDisplay = async (deviceId, deviceName) => {
    try {
      const response = await fetch(`${API_URL}/displays/${deviceId}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        alert(`已發送重整指令到「${deviceName}」`);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      alert('重整失敗：' + err.message);
    }
  };

  return (
    <div className="device-management">
      <div className="header">
        <h1>設備管理</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-secondary"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? '同步中...' : '🔄 同步 Google Sheets'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            ➕ 新增設備
          </button>
        </div>
      </div>

      <div className="filters">
        <label>篩選公司:</label>
        <select
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value)}
          className="company-filter"
        >
          <option value="all">全部公司</option>
          {companies.map(company => (
            <option key={company._id} value={company._id}>
              {company.名稱}
            </option>
          ))}
        </select>
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingDevice ? '編輯設備' : '新增設備'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>所屬公司 *</label>
                <select
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                  required
                  disabled={!!editingDevice}
                >
                  <option value="">請選擇公司</option>
                  {companies.map(company => (
                    <option key={company._id} value={company._id}>
                      {company.名稱}
                    </option>
                  ))}
                </select>
                {editingDevice && (
                  <small className="help-text">所屬公司建立後無法修改</small>
                )}
              </div>

              <div className="form-group">
                <label>設備代碼 *</label>
                <input
                  type="text"
                  value={formData.deviceCode}
                  onChange={(e) => setFormData({ ...formData, deviceCode: e.target.value })}
                  required
                  disabled={!!editingDevice}
                  placeholder="例: DEVICE001"
                />
                {editingDevice && (
                  <small className="help-text">設備代碼建立後無法修改</small>
                )}
              </div>

              <div className="form-group">
                <label>設備名稱 *</label>
                <input
                  type="text"
                  value={formData.deviceName}
                  onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                  required
                  placeholder="例: 一樓大廳顯示器"
                />
              </div>

              <div className="form-group">
                <label>綁定計時器 (選填)</label>
                <select
                  value={formData.timerId || ''}
                  onChange={(e) => setFormData({ ...formData, timerId: e.target.value || null })}
                >
                  <option value="">無</option>
                  {timers.map(timer => (
                    <option key={timer.id} value={timer.id}>
                      {timer.name}
                    </option>
                  ))}
                </select>
                <small className="help-text">選擇後此設備將顯示該計時器</small>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingDevice ? '更新' : '建立'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="devices-list">
        {filteredDevices.length === 0 ? (
          <div className="empty-state">
            <p>尚未建立任何設備</p>
            <p className="help-text">點擊上方「新增設備」按鈕開始建立</p>
          </div>
        ) : (
          <div className="devices-grid">
            {filteredDevices.map((device) => (
              <div key={device._id} className="device-card">
                <div className="device-header">
                  <h3>{device.名稱}</h3>
                  <span className="status-badge online">
                    {device.顯示器連結 ? '已建立' : '未建立'}
                  </span>
                </div>
                <div className="device-details">
                  <div className="detail-row">
                    <span className="label">設備 ID:</span>
                    <span className="value code">{device._id}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">所屬公司:</span>
                    <span className="value">{getCompanyName(device.公司ID)}</span>
                  </div>
                  {device.顯示器連結 && (
                    <div className="detail-row">
                      <span className="label">顯示器連結:</span>
                      <span className="value">
                        <a href={device.顯示器連結} target="_blank" rel="noopener noreferrer">
                          開啟顯示器
                        </a>
                      </span>
                    </div>
                  )}
                  {device.createdAt && (
                    <div className="detail-row">
                      <span className="label">建立時間:</span>
                      <span className="value">
                        {new Date(device.createdAt).toLocaleString('zh-TW')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="device-actions">
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEdit(device)}
                  >
                    編輯
                  </button>
                  {device.顯示器連結 && (
                    <>
                      <button
                        className="btn btn-small btn-primary"
                        onClick={() => window.open(device.顯示器連結, '_blank')}
                      >
                        查看
                      </button>
                      <button
                        className="btn btn-small btn-warning"
                        onClick={() => handleRefreshDisplay(device._id, device.名稱)}
                        title="強制遠端顯示器重新載入資料"
                      >
                        🔄 重整
                      </button>
                    </>
                  )}
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(device._id)}
                  >
                    刪除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceManagement;
